import type { Paragraph } from '@/api/types/stowed-element'
import { summarizeRowsLeading } from '@/lib/text/leading'
import type { RowsLeadingSummary } from '@/lib/text/leading'

type RunPropertyKey =
  'pointSize' | 'underline' | 'kerning' | 'tracking' | 'leading' | 'fontId' | 'fontFamilyId'

// kerning/tracking are absent-means-zero fields; leading is absent-means-"Metric". An unset run
// is a real, comparable value (its default), not "no opinion" — mirrors the normalization in
// `selectionStyleFromRanges` (text-style-engine.ts) so mixed-state detection agrees whether the
// selection comes from an active caret/range or a Select-Tool box selection that isn't editing.
const RUN_PROPERTIES_WITH_DEFAULT = new Set<RunPropertyKey>(['kerning', 'tracking', 'leading'])

function normalizeRunPropertyValue<K extends RunPropertyKey>(key: K, val: unknown): unknown {
  if (key === 'leading') return !val || val === 'Metric' ? 'Metric' : val
  if (RUN_PROPERTIES_WITH_DEFAULT.has(key)) return val ?? 0

  return val
}

export function getFirstRunProperty<K extends RunPropertyKey>(
  paragraphs: Paragraph[] | undefined,
  key: K
): unknown {
  if (!paragraphs) return undefined
  for (const p of paragraphs) {
    if (!p.textLines) continue
    for (const line of p.textLines) {
      if (!line.textRuns) continue
      for (const run of line.textRuns) {
        if (run[key] != null) return run[key]
      }
    }
  }

  return undefined
}

export function isRunPropertyMixed<K extends RunPropertyKey>(
  paragraphs: Paragraph[] | undefined,
  key: K
): boolean {
  if (!paragraphs) return false

  const hasDefault = RUN_PROPERTIES_WITH_DEFAULT.has(key)
  let first: unknown = undefined
  let found = false

  for (const p of paragraphs) {
    if (!p.textLines) continue
    for (const line of p.textLines) {
      if (!line.textRuns) continue
      for (const run of line.textRuns) {
        const raw = run[key]

        if (raw == null && !hasDefault) continue

        const val = hasDefault ? normalizeRunPropertyValue(key, raw) : raw

        if (!found) {
          first = val
          found = true
        } else if (val !== first) {
          return true
        }
      }
    }
  }

  return false
}

/** Leading of a whole-box selection — see `summarizeRowsLeading`. There is no layout here, so each
 *  stored line (one per paragraph once loaded) is read as one row. */
export function paragraphsLeading(paragraphs: Paragraph[] | undefined): RowsLeadingSummary | null {
  return summarizeRowsLeading(
    (paragraphs ?? []).flatMap((p) => (p.textLines ?? []).map((line) => line.textRuns ?? []))
  )
}
